import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { documentAPI, facilityAPI } from '../services/api';
import { Upload, FileText, Trash2, CheckCircle, Clock, AlertCircle, X } from 'lucide-react';
import { toast } from 'react-toastify';

function DocumentUpload() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const preselectedFacility = params.get('facilityId');

  const [documents, setDocuments] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(preselectedFacility || '');
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const pollRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [docsRes, facilitiesRes] = await Promise.all([
        documentAPI.getAll(),
        facilityAPI.getAll()
      ]);
      const docs = docsRes.data.documents || [];
      setDocuments(docs);
      setFacilities(facilitiesRes.data.facilities || []);

      // Check if any docs are still being analyzed
      const pending = docs.filter(d => !d.analyzed && !d.processed && d.status !== 'error');
      if (pending.length > 0) {
        setAnalyzing(true);
        // Start polling if not already
        if (!pollRef.current) {
          let pollAttempts = 0;
          const MAX_POLL_ATTEMPTS = 30; // 30 × 10s = 5 minutes max
          pollRef.current = setInterval(async () => {
            pollAttempts++;
            try {
              const res = await documentAPI.getAll();
              const updated = res.data.documents || [];
              setDocuments(updated);
              const stillPending = updated.filter(d => !d.analyzed && !d.processed && d.status !== 'error');
              if (stillPending.length === 0) {
                clearInterval(pollRef.current);
                pollRef.current = null;
                setAnalyzing(false);
                toast.success('✅ Permit analysis complete! Tasks and sources have been created.');
              } else if (pollAttempts >= MAX_POLL_ATTEMPTS) {
                clearInterval(pollRef.current);
                pollRef.current = null;
                setAnalyzing(false);
                toast.warning('Analysis is taking longer than expected. Please refresh the page to check status.');
              }
            } catch (e) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              setAnalyzing(false);
            }
          }, 30000); // Poll every 30 seconds
        }
      } else {
        setAnalyzing(false);
      }
    } catch (err) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are supported');
      return;
    }
    if (!selectedFacility) {
      toast.error('Please select a facility first');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('facilityId', selectedFacility);
      formData.append('documentType', 'air_permit');
      await documentAPI.upload(formData);
      toast.success('Permit uploaded! AI analysis starting...');
      setAnalyzing(true);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) handleUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await documentAPI.delete(id);
      toast.success('Document deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const getStatusIcon = (doc) => {
    if (doc.analyzed || doc.processed) return <CheckCircle size={15} color="#16A34A" />;
    if (doc.status === 'error') return <AlertCircle size={15} color="#DC2626" />;
    return <Clock size={15} color="#D97706" />;
  };

  const getStatusText = (doc) => {
    if (doc.analyzed || doc.processed) return 'Analyzed';
    if (doc.status === 'error') return 'Error';
    return 'Processing...';
  };

  const getStatusStyle = (doc) => {
    if (doc.analyzed || doc.processed) return { background: '#F0FDF4', color: '#16A34A' };
    if (doc.status === 'error') return { background: '#FEF2F2', color: '#DC2626' };
    return { background: '#FFFBEB', color: '#D97706' };
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading...</div>
  );

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div className="page-title">Documents</div>
        <div className="page-subtitle">Upload air permits for AI analysis and task generation</div>
      </div>

      {/* Upload area */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <label className="form-label">Select Facility *</label>
          <select
            value={selectedFacility}
            onChange={e => setSelectedFacility(e.target.value)}
            style={{ maxWidth: 380 }}
          >
            <option value="">Choose a facility...</option>
            {facilities.map(f => (
              <option key={f.id} value={f.id}>{f.name || f.facility_name} {f.state ? `· ${f.state}` : ''}</option>
            ))}
          </select>
        </div>

        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${dragOver ? '#0066FF' : '#E2E8F0'}`,
            borderRadius: 12,
            padding: '40px 20px',
            textAlign: 'center',
            background: dragOver ? '#EFF6FF' : '#FAFAFA',
            transition: 'all 0.15s',
            cursor: 'pointer'
          }}
          onClick={() => document.getElementById('file-input').click()}
        >
          <input id="file-input" type="file" accept=".pdf" onChange={handleFileInput} style={{ display: 'none' }} />
          {uploading ? (
            <div>
              <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid #E2E8F0', borderTopColor: '#0066FF', animation: 'spin 1s linear infinite', margin: '0 auto 14px' }} />
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#0A1628', marginBottom: 4 }}>Uploading & analyzing...</div>
              <div style={{ fontSize: 14, color: '#6B7280' }}>AI is reading your permit</div>
            </div>
          ) : (
            <div>
              <Upload size={40} color="#CBD5E1" style={{ margin: '0 auto 14px', display: 'block' }} />
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#0A1628', marginBottom: 4 }}>
                Drop your air permit here
              </div>
              <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 14 }}>or click to browse · PDF files only</div>
              <div className="btn btn-primary" style={{ display: 'inline-flex' }}>
                <Upload size={15} /> Choose File
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: 12, padding: '10px 14px', background: '#EFF6FF', borderRadius: 8, fontSize: 14, color: '#1E40AF', fontFamily: 'var(--font-body)' }}>
          💡 After upload, AI extracts compliance tasks, emission limits, and deadlines automatically.
          {analyzing && (
            <div style={{ marginTop: 12, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 16, height: 16, border: '2px solid #0066FF', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#1D4ED8', fontWeight: 500, fontFamily: 'var(--font-body)' }}>AI is analyzing your permit. This page will update automatically when complete...</span>
            </div>
          )}
        </div>
      </div>

      {/* Documents list */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div className="section-label" style={{ margin: 0 }}>Uploaded documents ({documents.length})</div>
        </div>

        {documents.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 48 }}>
            <FileText size={40} color="#CBD5E1" style={{ margin: '0 auto 14px', display: 'block' }} />
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: '#0A1628', marginBottom: 6 }}>No documents yet</div>
            <div style={{ fontSize: 14, color: '#6B7280' }}>Upload your first air permit above to get started</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {documents.map(doc => (
              <div key={doc.id} className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FileText size={17} color="#0066FF" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: '#0A1628', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {doc.document_name || doc.name}
                  </div>
                  <div style={{ fontSize: 13, color: '#6B7280', display: 'flex', gap: 12 }}>
                    {doc.facility_name && <span>📍 {doc.facility_name}</span>}
                    {doc.upload_date && <span>📅 {new Date(doc.upload_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                    {doc.file_size && <span>{(doc.file_size / 1024 / 1024).toFixed(1)} MB</span>}
                  </div>
                </div>
                <span className="badge" style={getStatusStyle(doc)}>
                  {getStatusIcon(doc)}
                  <span style={{ marginLeft: 4 }}>{getStatusText(doc)}</span>
                </span>
                <button onClick={() => handleDelete(doc.id)} className="icon-btn" style={{ color: '#DC2626', borderColor: '#FECACA' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default DocumentUpload;
